<template>
  <div class="container-fluid page-container">
    <ban-edit-profile v-if="isBannedFromEditing" />
    <div v-else-if="!busy">
      <div class="row page-header__container mb-4 pb-3">
        <page-heading :class="'col align-items-center'" :title="pageTitle" />
      </div>
      <div class="row">
        <div class="col">
          <validation-observer ref="companyDetailsObserver">
            <form @submit.prevent="onSave">
              <div class="col-12 p-4 shadow rounded bg-white border">
                <div class="row">
                  <div class="col-12 col-md-12 col-lg-7">
                    <div class="row">
                      <div class="col col-12 col-md-2">
                        <b-form-group :label="`${$t('Title *')}`" label-for="titleInput">
                          <b-form-select id="titleInput" v-model="title" :options="availableTitles" />
                        </b-form-group>
                      </div>

                      <div class="col col-12 col-md-4">
                        <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                          <b-form-group :label="`${$t('First Name *')}`" label-for="firstNameInput">
                            <b-form-input
                              id="firstNameInput"
                              v-model="firstName"
                              v-strip-url
                              :state="(dirty || validated) ? (valid ? null : false) : null"
                            />
                            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col col-12 col-md-6">
                        <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                          <b-form-group :label="`${$t('Last Name *')}`" label-for="lastNameInput">
                            <b-form-input
                              id="lastNameInput"
                              v-model="lastName"
                              v-strip-url
                              :state="(dirty || validated) ? (valid ? null : false) : null"
                            />
                            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col col-12 col-md-6">
                        <b-form-group :label="$t('Website/URL')" label-for="websiteInput">
                          <b-form-input id="websiteInput" v-model="website" />
                        </b-form-group>
                      </div>

                      <div class="col col-12 col-md-6">
                        <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" :rules="`required|valid-phone-number:${countryIsoCode}`">
                          <b-form-group :label="`${$t('Telephone Number *')}`" label-for="telephoneInput">
                            <b-form-input
                              id="telephoneInput"
                              v-model="telephone"
                              v-strip-url
                              type="tel"
                              :state="(dirty || validated) ? (valid ? null : false) : null"
                            />
                            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col col-12 col-md-6">
                        <b-form-group :label="$t('Company Type')" label-for="typeInput">
                          <validation-provider vid="companyTypes">
                            <b-form-select id="typeInput" v-model="companyType">
                              <optgroup v-for="(group, name) in companyTypes" :key="name" :label="name">
                                <option v-for="option in group" :key="option.value" :value="option.value">
                                  {{ option.name }}
                                </option>
                              </optgroup>
                            </b-form-select>
                          </validation-provider>
                        </b-form-group>
                      </div>

                      <div class="col col-12 col-md-6">
                        <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                          <b-form-group label-for="companyNameInput">
                            <template v-slot:label>
                              {{ $t('Company Name *') }}
                              <span class="info-icon mr-0">
                                <font-awesome-icon id="allow_edit_company_name" icon="info-circle" />
                              </span>
                            </template>
                            <b-form-input
                              id="companyNameInput"
                              v-model="companyName"
                              v-strip-url
                              :state="(dirty || validated) ? (valid ? null : false) : null"
                              :disabled="true"
                            />
                            <b-tooltip target="allow_edit_company_name" triggers="hover click">
                              {{ $t('To update this information, please contact your account manager or our team') }}
                              <router-link :to="{name: 'contact'}">
                                {{ $t('here') }}
                              </router-link>
                            </b-tooltip>
                            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col col-12 col-md-6">
                        <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" :rules="`required|valid-phone-number:${countryIsoCode}`">
                          <b-form-group :label="$t('Mobile Number')" label-for="mobileInput">
                            <b-form-input
                              id="mobileInput"
                              v-model="mobile"
                              v-strip-url
                              type="tel"
                              :state="(dirty || validated) ? (valid ? null : false) : null"
                            />
                            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col col-12 col-md-6">
                        <validation-provider v-if="shouldShowCompanyNumber" v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                          <b-form-group label-for="companyNumberInput">
                            <b-form-input
                              id="companyNumberInput"
                              v-model="companyNumber"
                              v-strip-url
                              :state="(dirty || validated) ? (valid ? null : false) : null"
                              :disabled="true"
                            />
                            <template v-slot:label>
                              {{ $t('Company Number *') }}
                              <span class="info-icon mr-0">
                                <font-awesome-icon id="allow_edit_company_number" icon="info-circle" />
                              </span>
                            </template>
                            <b-tooltip target="allow_edit_company_number" triggers="hover click">
                              {{ $t('To update this information, please contact your account manager or our team') }}
                              <router-link :to="{name: 'contact'}">
                                {{ $t('here') }}
                              </router-link>
                            </b-tooltip>
                            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col col-12 col-md-6">
                        <validation-provider v-slot="{valid, invalid, errors, dirty, validated}" rules="is-vat-number">
                          <b-form-group label-for="vatNumberInput">
                            <template v-slot:label>
                              {{ $t('VAT Number') }}
                              <span class="info-icon mr-0">
                                <font-awesome-icon id="allow_edit_vat_number" icon="info-circle" />
                              </span>
                            </template>
                            <b-form-input
                              id="vatNumberInput"
                              v-model="vatNumber"
                              v-strip-url
                              :state="(dirty || validated) ? (valid ? null : false) : null"
                              :disabled="true"
                            />
                            <b-tooltip target="allow_edit_vat_number" triggers="hover click">
                              {{ $t('To update this information, please contact your account manager or our team') }}
                              <router-link :to="{name: 'contact'}">
                                {{ $t('here') }}
                              </router-link>
                            </b-tooltip>
                            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col col-12">
                        <hr class="w-100">
                      </div>

                      <div class="col col-12 col-md-6">
                        <b-form-group label-for="communications-language">
                          <template v-slot:label>
                            {{ $t('Communications Language') }}
                            <span class="info-icon mr-0">
                              <font-awesome-icon id="allow_edit_communications_language" icon="info-circle" />
                            </span>
                          </template>
                          <b-form-select
                            id="communications-language"
                            v-model="communicationLanguageId"
                            :options="communicationLanguageOptions"
                            :disabled="true"
                          />
                          <b-tooltip target="allow_edit_communications_language" triggers="hover click">
                            {{ $t('To update this information, please contact your account manager or our team') }}
                            <router-link :to="{name: 'contact'}">
                              {{ $t('here') }}
                            </router-link>
                          </b-tooltip>
                        </b-form-group>
                      </div>

                      <div class="col col-12 col-md-6">
                        <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                          <b-form-group :label="$t('Languages Accepted')" label-for="accepted-languages">
                            <b-form-select
                              id="accepted-languages"
                              v-model="languagesAcceptedIds"
                              :multiple="true"
                              :options="languagesAcceptedOptions"
                              :select-size="4"
                              :state="(dirty || validated) ? (valid ? null : false) : null"
                            />
                            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col col-12">
                        <hr class="w-100">
                      </div>

                      <div class="col col-12 col-md-12 mt-2 mb-3">
                        <div class="row">
                          <div class="col col-12 mb-3">
                            <b-alert variant="warning" :show="true">
                              {{ $t('To update this information, please contact your account manager or our team') }}
                              <router-link :to="{name: 'contact'}">
                                {{ $t('here') }}
                              </router-link>
                            </b-alert>
                          </div>
                          <div class="col col-12 mb-3">
                            <b-form-checkbox v-model="billingAddressEnabled" class="mr-2" switch disabled>
                              <span class="show" v-html="$t('Use different billing address')" />
                            </b-form-checkbox>
                          </div>
                          <div class="col col-6">
                            <h2 class="h4">
                              {{ $t('Contact Address') }}
                            </h2>
                            <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                              <b-form-group :label="$t('Country')" label-for="country">
                                <b-form-select
                                  id="country"
                                  v-model="selectedCountryId"
                                  :options="countryOptions"
                                  :state="(dirty || validated) ? (valid ? null : false) : null"
                                  :disabled="true"
                                />
                                <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                            <address-finder :country="countryIsoCode" :disabled="true" @addressSelected="onAddressSelected" />
                            <div ref="manualAddressInput" class="row">
                              <div class="col-md-12">
                                <h2 class="h5 mb-0 py-3">
                                  {{ $t('Or manually enter address') }}
                                </h2>
                                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                                  <b-form-group :label="`${$t('Address Line 1 *')}`" label-for="addressLine1Input">
                                    <b-form-input
                                      id="addressLine1Input"
                                      v-model="addressLine1"
                                      v-strip-url
                                      :state="(dirty || validated) ? (valid ? null : false) : null"
                                      :disabled="true"
                                    />
                                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                      {{ errors[0] }}
                                    </b-form-invalid-feedback>
                                  </b-form-group>
                                </validation-provider>
                                <b-form-group :label="$t('Address Line 2')" label-for="addressLine2Input">
                                  <b-form-input id="addressLine2Input" v-model="addressLine2" :disabled="true" />
                                </b-form-group>
                                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                                  <b-form-group :label="`${$t('Town/City *')}`" label-for="townInput">
                                    <b-form-input
                                      id="townInput"
                                      v-model="addressTown"
                                      v-strip-url
                                      :state="(dirty || validated) ? (valid ? null : false) : null"
                                      :disabled="true"
                                    />
                                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                      {{ errors[0] }}
                                    </b-form-invalid-feedback>
                                  </b-form-group>
                                </validation-provider>
                                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                                  <b-form-group :label="`${$t('Postcode *')}`" label-for="postcodeInput">
                                    <b-form-input
                                      id="postcodeInput"
                                      v-model="addressPostcode"
                                      :state="(dirty || validated) ? (valid ? null : false) : null"
                                      :disabled="true"
                                    />
                                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                      {{ errors[0] }}
                                    </b-form-invalid-feedback>
                                  </b-form-group>
                                </validation-provider>
                              </div>
                            </div>
                          </div>
                          <div v-if="billingAddressEnabled" class="col col-6 billingAddressFields">
                            <h2 class="h4">
                              {{ $t('Billing Address') }}
                            </h2>
                            <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                              <b-form-group :label="`${$t('Country *')}`" label-for="billing-address-country-input">
                                <b-form-select
                                  id="billing-address-country-input"
                                  v-model="billingAddressCountryIsoCode"
                                  :state="(dirty || validated) ? (valid ? null : false) : null"
                                  :disabled="true"
                                >
                                  <option v-for="country in billingAddressCountries" :key="country.iso_code" :value="country.iso_code">
                                    {{ country.name }}
                                  </option>
                                </b-form-select>
                                <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                            <address-finder
                              :disabled="true"
                              :country="billingAddressCountryIsoCode"
                              @addressSelected="onBillingAddressSelected"
                            />
                            <div ref="manualAddressInput" class="row">
                              <div class="col-md-12">
                                <h2 class="h5 mb-0 py-3">
                                  {{ $t('Or manually enter address') }}
                                </h2>
                                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                                  <b-form-group :label="`${$t('Address Line 1 *')}`" label-for="billingAddressLine1Input">
                                    <b-form-input
                                      id="billingAddressLine1Input"
                                      v-model="billingAddressLine1"
                                      :state="(dirty || validated) ? (valid ? null : false) : null"
                                      :disabled="true"
                                    />
                                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                      {{ errors[0] }}
                                    </b-form-invalid-feedback>
                                  </b-form-group>
                                </validation-provider>
                                <b-form-group :label="$t('Address Line 2')" label-for="billingAddressLine2Input">
                                  <b-form-input id="billingAddressLine2Input" v-model="billingAddressLine2" :disabled="true" />
                                </b-form-group>
                                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                                  <b-form-group :label="`${$t('Town/City *')}`" label-for="billingAddressTownInput">
                                    <b-form-input
                                      id="billingAddressTownInput"
                                      v-model="billingAddressTown"
                                      :state="(dirty || validated) ? (valid ? null : false) : null"
                                      :disabled="true"
                                    />
                                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                      {{ errors[0] }}
                                    </b-form-invalid-feedback>
                                  </b-form-group>
                                </validation-provider>
                                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                                  <b-form-group :label="`${$t('Postcode *')}`" label-for="billingAddressPostcodeInput">
                                    <b-form-input
                                      id="billingAddressPostcodeInput"
                                      v-model="billingAddressPostcode"
                                      :state="(dirty || validated) ? (valid ? null : false) : null"
                                      :disabled="true"
                                    />
                                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                      {{ errors[0] }}
                                    </b-form-invalid-feedback>
                                  </b-form-group>
                                </validation-provider>

                                <div class="row">
                                  <div class="col-8">
                                    <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                                      <b-form-group :label="`${$t('Postcode *')}`" label-for="postcodeInput">
                                        <b-form-input
                                          id="postcodeInput"
                                          v-model="addressPostcode"
                                          v-strip-url
                                          :state="(dirty || validated) ? (valid ? null : false) : null"
                                          :disabled="true"
                                        />
                                        <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                          {{ errors[0] }}
                                        </b-form-invalid-feedback>
                                      </b-form-group>
                                    </validation-provider>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-12 mt-3 pl-0 nav__sticky-footer">
                  <b-button variant="outline-secondary" size="sm" class="reset-container" :disabled="busy" @click="onResetValues">
                    {{ $t('Cancel') }}
                  </b-button>
                  <div class="d-inline-block text-center ml-3 submit-container">
                    <b-button
                      size="sm"
                      :disabled="busy"
                      variant="primary"
                      :class="['animated-submit', submitClassObject, {'animate': animateSubmitButton}]"
                      type="submit"
                    >
                      {{ $t('Save') }}
                    </b-button>
                  </div>
                </div>
              </div>
            </form>
          </validation-observer>
        </div>
      </div>
    </div>
    <spinner v-else />
  </div>
</template>

<script>
import AddressFinder from '@/components/form/AddressFinder';
import PageHeading from "@/components/control/PageHeading";
import Spinner from "@/components/animation/Spinner";
import notification from "@/imports/mixins/notification";
import isBannedFromEditing from "@/imports/mixins/isBannedFromEditing";
import BanEditProfile from "@/components/control/BanEditProfile";
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import submitAnimationHandler from "@/imports/mixins/submitAnimationHandler";
import company from "@/imports/mixins/company";

export default {
  name: "CompanyDetails",
  components: {AddressFinder, PageHeading, Spinner, ValidationProvider, ValidationObserver, BanEditProfile},
  mixins:[notification, isBannedFromEditing, submitAnimationHandler, company],
  data() {
    return {
      pageTitle: this.$t("Company Details"),
      title: '',
      firstName: '',
      lastName: '',
      website: '',
      telephone: '',
      mobile: '',
      companyName: '',
      companyType: '',
      companyNumber: '',
      addressFinder: '',
      addressLine1: '',
      addressLine2: '',
      addressTown: '',
      addressPostcode: '',
      countryIsoCode: '',
      billingAddressLine1: '',
      billingAddressLine2: '',
      billingAddressTown: '',
      billingAddressCounty: '',
      billingAddressPostcode: '',
      billingAddressCountry: '',
      billingAddressCountryIsoCode: '',
      billingAddressEnabled: false,
      billingAddressCountries: [],
      countries: [],
      selectedCountryId: '',
      languagesAcceptedIds: [],
      communicationLanguageId: '',
      vatNumber: '',
      availableTitles: [{value: '', text: 'Please choose', disabled: true}, 'Mr','Mrs','Miss','Ms', 'Dr'],
      busy: true,
    }
  },
  computed: {
    shouldShowCompanyNumber() {
      const typesToShow = ['Ltd', 'Plc', 'CIC', 'PrivateLtd', 'Unlimited'];
      return typesToShow.includes(this.companyType);
    },
    isMobile: function () {
      return this.$store.state.IS_MOBILE;
    },
    countryOptions() {
      return this.countries.map((country) => ({value: country.id, text: this.$t(country.name)}));
    },
    country() {
      return this.countries.filter((country) => country.id === this.selectedCountryId)[0];
    },
    communicationLanguageOptions() {
      return this.languageOptions.length === 1
        ? this.languageOptions
        : [{value: null, text: this.$t('Please select a language')}, ...this.languageOptions];
    },
    languageOptions() {
      return this.country.languages.map((language) => ({value: language.id, text: this.$t(language.name)}));
    },
    languagesAcceptedOptions() {
      return this.languageOptions;
    },
    languages() {
      return this.country.languages;
    },
    companyTypes() {
      return {
        [this.$t('Standard Types')]: [
          {value:"Ltd", name: this.$t('Ltd (Private Limited Company)')},
          {value:"SoleTrader", name: this.$t('Sole Trader')},
          {value:"Partnership", name: this.$t('General Partnership')},
          {value:"Plc", name: this.$t('Plc (Public Limited Company)')},
        ],
        [this.$t('Other Types')]: [
          {value:'CIC', name: this.$t('CIC (Community Interest Company)')},
          {value:'Coop', name: this.$t('Co-operative')},
          {value:'LLP', name: this.$t('LLP (Limited Liability Partnership)')},
          {value:'LP', name: this.$t('LP (Limited Partnership)')},
          {value:'PrivateLtd', name: this.$t('Private Company Limited by Guarantee')},
          {value:'Unlimited', name: this.$t('Unlimited Company')},
          {value:'Other', name: this.$t('Other (we will ask you about this)')},
        ]
      }
    },
    billingAddress() {
      return this.company.addresses.filter((address) => address.type === 'billing')[0];
    },
    billingAddressIsEnabled() {
      return this.company.addresses.filter((address) => address.type === 'billing').length === 1;
    }
  },
  methods: {
    onResetValues() {
      this.addressLine1 = this.company.address_line1;
      this.addressLine2 = this.company.address_line2;
      this.addressTown = this.company.town;
      this.addressPostcode = this.company.postcode;

      this.billingAddressEnabled = this.billingAddressIsEnabled;
      this.billingAddressLine1 = this.billingAddress?.address_line1;
      this.billingAddressLine2 = this.billingAddress?.address_line2;
      this.billingAddressTown = this.billingAddress?.town;
      this.billingAddressCounty = this.billingAddress?.county;
      this.billingAddressPostcode = this.billingAddress?.postcode;
      this.billingAddressCountry = this.billingAddress?.country;
      this.billingAddressCountryIsoCode = this.billingAddress?.country.iso_code;
      this.billingAddressEnabled = this.billingAddressIsEnabled;
      this.$api.country.fetchAll().then((result) => {
        this.billingAddressCountries = result.data.data[0];
      });

      this.telephone = this.company.phone;
      this.mobile = this.company.mobile;
      this.companyName = this.company.internal_name;
      this.title = this.company.company_user.title;
      this.firstName = this.company.company_user.forename;
      this.lastName = this.company.company_user.surname;
      this.companyType = this.company.company_user.company_type;
      this.companyNumber = this.company.company_user.company_number;
      this.countryIsoCode = this.company.country.iso_code;
      this.selectedCountryId = this.company.country.id;
      this.communicationLanguageId = this.company.communications_language.id;
      this.languagesAcceptedIds = this.company.languages_accepted.map((language) => language.id);
      this.countries = this.company.business_unit.countries;
      this.vatNumber = this.company.vat_number;
      for (const listing of this.company.listings) {
        if (listing.website !== null) {
          this.website = listing.website;
          break;
        }
      }
    },
    onSave() {
      this.$refs.companyDetailsObserver.validate().then(success => {
        if (!success) {
          return;
        }
        this.animateSubmitButton = true;

        let languages_accepted = this.languages.filter(
          (language) => this.languagesAcceptedIds.includes(language.id)
        ).map((language) => language.id);
        let communications_language = this.languages.filter(
          (language) => language.id === this.communicationLanguageId
        ).map((language) => language.id)[0];

        const value = {
          address_line1: this.addressLine1,
          address_line2: this.addressLine2,
          town: this.addressTown,
          postcode: this.addressPostcode,
          phone: this.telephone,
          mobile: this.mobile,
          internal_name: this.companyName,
          website: this.website,
          country: this.country,
          languages_accepted: languages_accepted,
          communications_language: communications_language,
          vat_number: this.vatNumber,
          company_user: {
            company_type: this.companyType,
            company_number: this.companyNumber,
            title: this.title,
            forename: this.firstName,
            surname: this.lastName,
          },
          billing_address_line1: this.billingAddressLine1,
          billing_address_line2: this.billingAddressLine2,
          billing_address_town: this.billingAddressTown,
          billing_address_postcode: this.billingAddressPostcode,
          billing_address_enabled: this.billingAddressEnabled,
          billing_address_country: this.billingAddressCountry,
          billing_address_country_iso: this.billingAddressCountryIsoCode,
        };

        this.busy = true;
        this.$store.dispatch('updateCompany', value)
          .then(success => {

            this.notify(
              this.$bvToast,
              success ? this.$t('Your details have been saved successfully') : this.$t('There was an error saving your details'),
              null,
              success ? 'success' : 'danger'
            );
            this.enableSubmitAnimation(success);
          })
          .finally(() => this.busy = false);
      });
    },
    onAddressSelected({line1, line2, town, postcode}) {
      this.addressLine1 = line1;
      this.addressLine2 = line2;
      this.addressTown = town;
      this.addressPostcode = postcode;
      if (this.isMobile) {
        this.$refs.manualAddressInput.scrollIntoView();
      }
    },
    onBillingAddressSelected({line1, line2, town, postcode}) {
      this.billingAddressLine1 = line1;
      this.billingAddressLine2 = line2;
      this.billingAddressTown = town;
      this.billingAddressPostcode = postcode;
      if (this.isMobile) {
        this.$refs.manualAddressInput.scrollIntoView();
      }
    },
    updateCommunicationsLanguageOptions(country) {
      let countryLanguages = country.languages;

      if (this.languages.length === 1) {
        this.communicationLanguageId = countryLanguages[0].id;
        return;
      }

      let languages = countryLanguages.filter(
        (language) => language.id === this.company.communications_language.id
      );

      if (languages.length === 0) {
        this.communicationLanguageId = null;
        return;
      }

      this.communicationLanguageId = languages[0].id;
    },
    updateLanguagesAcceptedOptions(country) {
      let countryLanguageIds = country.languages.map((language) => language.id);
      let languagesAcceptedIds = this.company.languages_accepted.map((language) => language.id);
      this.languagesAcceptedIds = countryLanguageIds.filter((languageId) => languagesAcceptedIds.includes(languageId));
    }
  },
  watch: {
    country(newValue) {
      this.updateCommunicationsLanguageOptions(newValue);
      this.updateLanguagesAcceptedOptions(newValue);
    }
  },
  created() {
    this.$store.dispatch('getCompany').then(() => {
      this.onResetValues();
      this.busy = false;
    });
  },
}
</script>

<style scoped>

.reset-container,
.submit-container {
  width: 100px;
}
</style>
